// Referrals' dialogs (docs/referrals-spec.md 4.4): Qualify (lock the amount), Mark paid (with the bank
// transfer's reference), Change (reassign who introduced them) and Add by hand, and the person search the last
// 2 share with Who can refer's "Add someone else". Each write is 1 of migration 79's staff writes, through
// src/admin/api.js; a refusal shows in the dialog in words (REF.refusals), and nothing is changed on the page
// until the database has said yes.
import { useEffect, useState } from 'react'
import { Button, Dialog, Field, SearchBox, Select, TextInput, useAction, useAdmin } from '../ui/index.js'
import { REF, STATUS_WORDS, dayMonthYear, gbp, poundsOf, refusal, suggestedAmount } from './words.js'

// staff_people by name or email, 6 at most, with a button on each: Pick (Change, Add by hand) or Tick
export function PersonSearch({ label, placeholder, buttonLabel, buttonAria, onPick, busyId = null, exclude = [] }) {
  const { api } = useAdmin()
  const [q, setQ] = useState('')
  const [found, setFound] = useState({ status: 'idle', rows: [] })
  useEffect(() => {
    const term = q.trim()
    if (term.length < 2) { setFound({ status: 'idle', rows: [] }); return undefined }
    let alive = true
    const t = setTimeout(() => {
      setFound(f => ({ ...f, status: 'loading' }))
      api.staff_people({ search: term, limit: 6 }).then(
        rows => { if (alive) setFound({ status: 'ready', rows: (rows || []).filter(r => !exclude.includes(r.person_id)) }) },
        e => { if (alive) setFound({ status: 'error', rows: [], error: api.words(e) }) },
      )
    }, 250)
    return () => { alive = false; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, api])
  return (
    <div className="rf-search">
      <SearchBox label={label} placeholder={placeholder} value={q} onChange={setQ} />
      {found.status === 'loading' && <span className="rf-search-note" role="status">{REF.reassign.looking}</span>}
      {found.status === 'error' && <span className="ad-error" role="alert">{found.error}</span>}
      {found.status === 'ready' && !found.rows.length && <span className="rf-search-note">{REF.reassign.nobody}</span>}
      {found.rows.length > 0 && (
        <ul className="rf-search-list" aria-label={label}>
          {found.rows.map(r => (
            <li key={r.person_id} className="rf-search-row">
              <span className="rf-search-who">
                <span className="rf-search-name">{r.full_name}</span>
                <span className="rf-search-email">{r.email}</span>
              </span>
              <Button size="sm" busy={busyId === r.person_id} aria-label={buttonAria ? buttonAria(r) : undefined} onClick={() => onPick(r)}>{buttonLabel}</Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export function QualifyDialog({ line, offer, onClose, onDone }) {
  const { api } = useAdmin()
  const pct = offer?.fee_kind === 'percentage'
  const [paid, setPaid] = useState('')
  const [amount, setAmount] = useState(() => suggestedAmount(offer))
  const [error, setError] = useState(null)
  const save = useAction(body => api.qualify_introduction(body))

  function onPaid(v) {
    setPaid(v)
    const n = poundsOf(v)
    if (n !== null) setAmount(suggestedAmount(offer, n))
  }
  async function go() {
    const n = poundsOf(amount)
    if (n === null) { setError(REF.qualify.error); return }
    setError(null)
    const answer = await save.run({ id: line.id, amount: n })
    if (answer) onDone(REF.flash.qualified(gbp(answer.payout_amount ?? n)))
  }
  return (
    <Dialog eyebrow={REF.qualify.eyebrow} title={REF.qualify.title} onClose={onClose} busy={save.busy}
      footer={<>
        <Button onClick={onClose} disabled={save.busy}>{REF.buttons.cancel}</Button>
        <Button kind="ink" busy={save.busy} onClick={go}>{REF.buttons.qualify}</Button>
      </>}>
      <p className="ad-lede">{REF.qualify.text(String(offer?.qualify_rule || '').trim())}</p>
      <p className="ad-lede">{REF.qualify.locks}</p>
      {pct && (
        <Field label={REF.qualify.paid} plain>
          {props => <TextInput {...props} inputMode="decimal" value={paid} onChange={onPaid} />}
        </Field>
      )}
      <Field label={REF.qualify.amount} plain error={error || (save.error && refusal(save.error))}>
        {props => <TextInput {...props} inputMode="decimal" value={amount} onChange={setAmount} />}
      </Field>
    </Dialog>
  )
}

export function PaidDialog({ line, onClose, onDone }) {
  const { api } = useAdmin()
  const [reference, setReference] = useState('')
  const save = useAction(body => api.mark_introduction_paid(body))
  async function go() {
    const answer = await save.run({ id: line.id, reference: reference.trim() })
    if (answer) onDone(REF.flash.paid)
  }
  return (
    <Dialog eyebrow={REF.paid.eyebrow} title={REF.paid.title(gbp(line.payout_amount))} onClose={onClose} busy={save.busy}
      footer={<>
        <Button onClick={onClose} disabled={save.busy}>{REF.buttons.cancel}</Button>
        <Button kind="ink" busy={save.busy} onClick={go}>{REF.buttons.markPaid}</Button>
      </>}>
      <p className="ad-lede">{REF.paid.text}</p>
      {line.refunded_at && <p className="rf-paid-refunded" role="note">{REF.paid.refunded(dayMonthYear(line.refunded_at))}</p>}
      <Field label={REF.paid.reference} plain error={save.error && refusal(save.error)}>
        {props => <TextInput {...props} value={reference} onChange={setReference} maxLength={80} autoComplete="off" />}
      </Field>
    </Dialog>
  )
}

export function ReassignDialog({ line, onClose, onDone }) {
  const { api } = useAdmin()
  const [pick, setPick] = useState(null)
  const save = useAction(body => api.reassign_introduction(body))
  async function go() {
    if (!pick) return
    const answer = await save.run({ id: line.id, referrer: pick.person_id })
    if (answer) onDone(REF.flash.reassigned)
  }
  return (
    <Dialog eyebrow={REF.reassign.eyebrow} title={REF.reassign.title} onClose={onClose} busy={save.busy}
      footer={<>
        <Button onClick={onClose} disabled={save.busy}>{REF.buttons.cancel}</Button>
        <Button kind="ink" busy={save.busy} disabled={!pick} onClick={go}>{REF.buttons.save}</Button>
      </>}>
      <p className="ad-lede">{REF.reassign.text}</p>
      <PersonSearch label={REF.reassign.search} buttonLabel={REF.buttons.pick} onPick={setPick}
        exclude={[line.referrer_person_id]} buttonAria={r => `Pick ${r.full_name}`} />
      {pick && <p className="rf-picked" role="status">{REF.reassign.picked(pick.full_name)}</p>}
      {save.error && <p className="ad-error" role="alert">{refusal(save.error)}</p>}
    </Dialog>
  )
}

export function AddDialog({ products, onClose, onDone }) {
  const { api } = useAdmin()
  const [who, setWho] = useState(null)
  const [product, setProduct] = useState('')
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('applied')
  const [error, setError] = useState(null)
  // what Add said is missing goes as soon as a field changes (the review of 18 Oct: it stayed once both were filled)
  const edit = set => v => { setError(null); set(v) }
  const save = useAction(body => api.add_introduction(body))
  async function go() {
    if (!who || !product) { setError(REF.add.needWho); return }
    if (!name.trim() && !email.trim()) { setError(REF.add.needName); return }
    setError(null)
    const answer = await save.run({ referrer: who.person_id, product, name: name.trim(), email: email.trim() || null, status })
    if (answer) onDone(REF.flash.added)
  }
  return (
    <Dialog eyebrow={REF.add.eyebrow} title={REF.add.title} onClose={onClose} busy={save.busy} wide
      footer={<>
        <Button onClick={onClose} disabled={save.busy}>{REF.buttons.cancel}</Button>
        <Button kind="ink" busy={save.busy} onClick={go}>{REF.buttons.add}</Button>
      </>}>
      <p className="ad-lede">{REF.add.text}</p>
      <div className="ad-field">
        <span className="ad-label ad-label--plain">{REF.add.who}</span>
        <PersonSearch label={REF.add.who} placeholder={REF.reassign.search} buttonLabel={REF.buttons.pick} onPick={edit(setWho)}
          buttonAria={r => `Pick ${r.full_name}`} />
        {who && <p className="rf-picked" role="status">{REF.reassign.picked(who.full_name)}</p>}
      </div>
      <Field label={REF.add.product} plain>
        {props => <Select {...props} value={product} onChange={edit(setProduct)} placeholder={REF.pickProduct}
          options={products.map(p => ({ value: p.id, label: p.name }))} />}
      </Field>
      <div className="ad-row">
        <Field label={REF.add.name} plain>{props => <TextInput {...props} value={name} onChange={edit(setName)} maxLength={200} autoComplete="off" />}</Field>
        <Field label={REF.add.email} plain>{props => <TextInput {...props} type="email" value={email} onChange={edit(setEmail)} maxLength={254} autoComplete="off" />}</Field>
      </div>
      <Field label={REF.add.status} plain>
        {props => <Select {...props} value={status} onChange={setStatus}
          options={['applied', 'joined', 'not_proceeding'].map(s => ({ value: s, label: STATUS_WORDS[s] }))} />}
      </Field>
      {(error || save.error) && <p className="ad-error" role="alert">{error || refusal(save.error)}</p>}
    </Dialog>
  )
}
